import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  ErrorState,
  Field,
  ProblemError,
  Skeleton,
  SuccessState,
} from "@csa/web-foundation";
import { useT } from "../i18n/index";
import type { MessageKey } from "../i18n/dictionaries";
import { usePartyPrincipal } from "../auth/party-principal";
import { useAppearance, type TextSize } from "../settings/appearance";
import { useChangePassword, useSetLoginOtp, useUpdateProfile } from "./use-profile";
import type { UpdateProfileInput } from "../api/contracts";

// Profil / Hesabım — the customer's own account surface (ADR 0005 §5 / WEB3). Calm,
// reassuring, Turkish-first. It renders the masked identity (view-only), lets the
// customer EDIT their name + e-mail (PATCH /v1/customer/profile), surfaces the concierge
// presence (Danışmanınız), and holds the Güvenlik panel: the login-OTP toggle
// (PATCH /v1/customer/settings/login-otp) + a real in-app password change.
//
// Şifre değiştir is a first-class in-app form → POST /v1/customer/change-password (the
// server verifies the current password before setting the new one). No Keycloak-console
// redirect anymore.
//
// Contract note: GET /v1/customer/profile now ALSO echoes email + phone (both nullable) —
// they prefill the edit form. E-mail is editable via the PATCH; phone is a possession
// channel omitted from the editable set (server rejects it) — shown read-only with the
// verification note.

export function Profile() {
  const { t, locale, setLocale } = useT();
  const { profile, isLoading, error } = usePartyPrincipal();
  const a11y = useAppearance();

  if (isLoading) {
    return (
      <Card data-testid="profile-loading">
        <Skeleton lines={5} />
      </Card>
    );
  }
  if (error || !profile) {
    return (
      <ErrorState>
        <p data-testid="profile-error">{t("pf.error")}</p>
      </ErrorState>
    );
  }

  const textSizes: TextSize[] = ["normal", "large"];

  return (
    <div className="stack" data-testid="profile">
      {/* ── Masked identity — view-only, no self-reveal ── */}
      <Card data-testid="profile-identity">
        <h1>{t("pf.title")}</h1>
        <div className="profile-field">
          <span className="muted small">{t("pf.name")}</span>
          <strong data-testid="profile-name">
            {profile.firstName} {profile.lastName}
          </strong>
        </div>
        <div className="profile-field">
          <span className="muted small">{t("pf.tckn")}</span>
          <span className="profile-masked" data-testid="profile-tckn">
            <span aria-hidden="true">🔒</span> {profile.tcknMasked ?? "—"}
          </span>
        </div>
        {/* The masking-as-message note (ⓘ) — protection framed, no reveal control exists. */}
        <p className="muted small" data-testid="masking-note">
          <span aria-hidden="true">ⓘ </span>
          {t("pf.tcknNote")}
        </p>
      </Card>

      {/* ── Editable personal info — name + e-mail (PATCH); phone read-only ── */}
      <ContactCard
        firstName={profile.firstName}
        lastName={profile.lastName}
        email={profile.email ?? null}
        phone={profile.phone ?? null}
        t={t as (k: MessageKey) => string}
      />

      {/* ── Concierge presence — the household's relationship-owner advisor by NAME
          (advisorDisplayName), a generic presence when the read carries none. ── */}
      <Card data-testid="advisor-presence">
        <h2>{t("pf.advisorTitle")}</h2>
        {profile.advisorDisplayName ? (
          <p data-testid="advisor-name">
            <strong>{profile.advisorDisplayName}</strong>
          </p>
        ) : null}
        <p className="muted small">{t("pf.advisorNote")}</p>
        {/* Dalga-1: in-app mesajlaşma yok — sahte "Mesaj gönder" CTA yerine dürüst,
            uca çıkmayan bilgi. Mesajlaşma yüzeyi geldiğinde CTA burada geri döner. */}
        <p className="muted small" data-testid="advisor-reach">
          {t("pf.advisorReach")}
        </p>
      </Card>

      {/* ── Güvenlik / account ── */}
      <SecurityCard
        loginOtpDisabled={profile.loginOtpDisabled ?? false}
        t={t as (k: MessageKey) => string}
      />

      {/* ── Settings ── */}
      <Card data-testid="settings">
        <h2>{t("set.title")}</h2>

        {/* Language — TR / EN via the shared i18n. */}
        <section className="settings-group" data-testid="settings-language">
          <h3>{t("set.langTitle")}</h3>
          <div className="settings-controls">
            <Button
              variant={locale === "tr" ? "primary" : "secondary"}
              aria-pressed={locale === "tr"}
              data-testid="lang-tr"
              onClick={() => setLocale("tr")}
            >
              {t("set.lang.tr")}
            </Button>
            <Button
              variant={locale === "en" ? "primary" : "secondary"}
              aria-pressed={locale === "en"}
              data-testid="lang-en"
              onClick={() => setLocale("en")}
            >
              {t("set.lang.en")}
            </Button>
          </div>
        </section>

        {/* Accessibility — UX-level, no backend. */}
        <section className="settings-group" data-testid="settings-a11y">
          <h3>{t("set.a11yTitle")}</h3>
          <div className="settings-row">
            <span>{t("set.textSize")}</span>
            <div className="settings-controls">
              {textSizes.map((s) => (
                <Button
                  key={s}
                  variant={a11y.textSize === s ? "primary" : "secondary"}
                  aria-pressed={a11y.textSize === s}
                  data-testid={`text-size-${s}`}
                  onClick={() => a11y.setTextSize(s)}
                >
                  {t(`set.textSize.${s}` as MessageKey)}
                </Button>
              ))}
            </div>
          </div>
          <div className="settings-row">
            <span>{t("set.reducedMotion")}</span>
            <Button
              variant={a11y.reducedMotion ? "primary" : "secondary"}
              aria-pressed={a11y.reducedMotion}
              data-testid="reduced-motion"
              onClick={() => a11y.setReducedMotion(!a11y.reducedMotion)}
            >
              {a11y.reducedMotion ? t("set.on") : t("set.off")}
            </Button>
          </div>
          <div className="settings-row">
            <span>{t("set.highContrast")}</span>
            <Button
              variant={a11y.highContrast ? "primary" : "secondary"}
              aria-pressed={a11y.highContrast}
              data-testid="high-contrast"
              onClick={() => a11y.setHighContrast(!a11y.highContrast)}
            >
              {a11y.highContrast ? t("set.on") : t("set.off")}
            </Button>
          </div>
        </section>

        {/* Links — the consent ledger (TF2) + KVKK data-rights (staff/ops-mediated). */}
        <section className="settings-group" data-testid="settings-links">
          <h3>{t("set.linksTitle")}</h3>
          <ul className="settings-links">
            <li>
              <Link to="/consents" data-testid="link-consents">
                {t("set.linkConsents")}
              </Link>
            </li>
            <li>
              <span data-testid="link-datarights">{t("set.linkDataRights")}</span>
              <p className="muted small">{t("set.dataRightsNote")}</p>
            </li>
          </ul>
        </section>
      </Card>
    </div>
  );
}

// ── Editable personal info. View → "Düzenle"; edit → name inputs + e-mail (PREFILLED from
// the enriched profile read) + phone read-only with the verification note. Kaydet → PATCH
// /v1/customer/profile → SuccessState flash + query refetch. Inline ProblemJson error (the
// `detail` text). Only changed/non-empty fields go in the patch.
function ContactCard({
  firstName,
  lastName,
  email,
  phone,
  t,
}: {
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  t: (k: MessageKey) => string;
}) {
  const update = useUpdateProfile();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({ firstName, lastName, email: email ?? "" });

  const openEdit = () => {
    setForm({ firstName, lastName, email: email ?? "" });
    setSaved(false);
    update.reset();
    setEditing(true);
  };
  const cancel = () => {
    update.reset();
    setEditing(false);
  };

  const onSave = () => {
    const patch: UpdateProfileInput = {};
    const fn = form.firstName.trim();
    const ln = form.lastName.trim();
    const em = form.email.trim();
    if (fn && fn !== firstName) patch.firstName = fn;
    if (ln && ln !== lastName) patch.lastName = ln;
    if (em && em !== (email ?? "")) patch.email = em;
    // The schema requires ≥1 field — nothing changed → just close (no empty PATCH).
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    update.mutate(patch, {
      onSuccess: () => {
        setSaved(true);
        setEditing(false);
      },
    });
  };

  const errText =
    update.error instanceof ProblemError
      ? update.error.problem.detail || t("pf.saveError")
      : update.error
        ? t("pf.saveError")
        : null;

  return (
    <Card data-testid="contact-card">
      <h2>{t("pf.contactTitle")}</h2>

      {saved && !editing ? (
        <SuccessState>
          <p data-testid="profile-success">{t("pf.saveOk")}</p>
        </SuccessState>
      ) : null}

      {editing ? (
        <form
          className="profile-form"
          data-testid="profile-form"
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
        >
          <Field
            label={t("pf.firstName")}
            value={form.firstName}
            data-testid="input-firstName"
            autoComplete="given-name"
            onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
          />
          <Field
            label={t("pf.lastName")}
            value={form.lastName}
            data-testid="input-lastName"
            autoComplete="family-name"
            onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
          />
          <Field
            label={t("pf.email")}
            type="email"
            value={form.email}
            hint={t("pf.emailHint")}
            placeholder={t("pf.emailPlaceholder")}
            data-testid="input-email"
            autoComplete="email"
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />

          {/* Phone — read-only, NOT in the editable set (possession channel). Prefilled
              from the enriched profile read; "—" when the party has none on file. */}
          <div className="profile-field">
            <span className="muted small">{t("pf.phone")}</span>
            <span data-testid="profile-phone">{phone || "—"}</span>
            <span className="muted small" data-testid="phone-note">
              {t("pf.phoneNote")}
            </span>
          </div>

          {errText ? (
            <p className="field-error" role="alert" data-testid="profile-save-error">
              {errText}
            </p>
          ) : null}

          <div className="settings-controls">
            <Button
              type="submit"
              variant="primary"
              data-testid="profile-save"
              disabled={update.isPending}
            >
              {update.isPending ? t("pf.saving") : t("pf.save")}
            </Button>
            <Button
              type="button"
              variant="secondary"
              data-testid="profile-cancel"
              onClick={cancel}
              disabled={update.isPending}
            >
              {t("pf.cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className="profile-field">
            <span className="muted small">{t("pf.name")}</span>
            <strong>
              {firstName} {lastName}
            </strong>
          </div>
          <div className="profile-field">
            <span className="muted small">{t("pf.phone")}</span>
            <span data-testid="profile-phone">{phone || "—"}</span>
            <span className="muted small" data-testid="phone-note">
              {t("pf.phoneNote")}
            </span>
          </div>
          <Button variant="secondary" data-testid="profile-edit-toggle" onClick={openEdit}>
            {t("pf.edit")}
          </Button>
        </>
      )}
    </Card>
  );
}

// ── Güvenlik / account. The login-OTP toggle (Giriş doğrulaması) reads loginOtpDisabled
// and writes PATCH /v1/customer/settings/login-otp; "enabled" = the OTP is REQUIRED
// (= NOT disabled). Şifre değiştir is a REAL in-app form → POST /v1/customer/change-password
// (the server verifies the current password server-side; the possession factor is untouched).
function SecurityCard({
  loginOtpDisabled,
  t,
}: {
  loginOtpDisabled: boolean;
  t: (k: MessageKey) => string;
}) {
  const setLoginOtp = useSetLoginOtp();
  const enabled = !loginOtpDisabled;

  return (
    <Card data-testid="security-card">
      <h2>{t("pf.securityTitle")}</h2>

      {/* Login-OTP toggle — the returning-login verification preference. */}
      <div className="settings-row" data-testid="login-otp-row">
        <div>
          <strong>{t("pf.loginOtpTitle")}</strong>
          <p className="muted small">{t("pf.loginOtpNote")}</p>
        </div>
        <Button
          variant={enabled ? "primary" : "secondary"}
          aria-pressed={enabled}
          data-testid="login-otp-toggle"
          disabled={setLoginOtp.isPending}
          onClick={() => setLoginOtp.mutate({ disabled: enabled })}
        >
          {enabled ? t("set.on") : t("set.off")}
        </Button>
      </div>
      {setLoginOtp.error ? (
        <p className="field-error" role="alert" data-testid="login-otp-error">
          {t("pf.loginOtpError")}
        </p>
      ) : null}

      {/* Şifre değiştir — a first-class in-app change (POST /v1/customer/change-password). */}
      <PasswordChangeSection t={t} />
    </Card>
  );
}

// ── Şifre değiştir — the in-app password-change form. View → "Şifre değiştir"; open →
// current + new + confirm (new min 6, confirm must match — checked client-side before the
// call). Kaydet → POST /v1/customer/change-password. Success → SuccessState ("Şifreniz
// güncellendi"). A wrong current password (400) → inline "Mevcut şifreniz hatalı"; a
// Keycloak-unconfigured 503 → the calm "şu an yapılamıyor" message. Passwords are held only
// in local form state and cleared on close/success (never persisted).
function PasswordChangeSection({ t }: { t: (k: MessageKey) => string }) {
  const change = useChangePassword();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [localError, setLocalError] = useState<MessageKey | null>(null);

  const reset = () => {
    setForm({ current: "", next: "", confirm: "" });
    setLocalError(null);
    change.reset();
  };
  const openForm = () => {
    reset();
    setDone(false);
    setOpen(true);
  };
  const cancel = () => {
    reset();
    setOpen(false);
  };

  const onSubmit = () => {
    setLocalError(null);
    if (form.next.length < 6) {
      setLocalError("pf.pwTooShort");
      return;
    }
    if (form.next !== form.confirm) {
      setLocalError("pf.pwMismatch");
      return;
    }
    change.mutate(
      { currentPassword: form.current, newPassword: form.next },
      {
        onSuccess: () => {
          setForm({ current: "", next: "", confirm: "" });
          setDone(true);
          setOpen(false);
        },
      },
    );
  };

  // Server errors are mapped by HTTP status (the code contract): 400 = wrong current
  // password; 503 = the surface is temporarily unconfigured; anything else → generic.
  const problem = change.error instanceof ProblemError ? change.error : null;
  const serverErrKey: MessageKey | null = problem
    ? problem.status === 503
      ? "pf.pwUnavailable"
      : problem.status === 400
        ? "pf.pwWrong"
        : "pf.pwError"
    : change.error
      ? "pf.pwError"
      : null;
  const errKey = localError ?? serverErrKey;

  return (
    <>
      <div className="settings-row" data-testid="password-row">
        <div>
          <strong>{t("pf.passwordTitle")}</strong>
          <p className="muted small">{t("pf.passwordNote")}</p>
        </div>
        {!open ? (
          <Button variant="secondary" data-testid="password-change" onClick={openForm}>
            {t("pf.passwordAction")}
          </Button>
        ) : null}
      </div>

      {done && !open ? (
        <SuccessState>
          <p data-testid="password-success">{t("pf.pwOk")}</p>
        </SuccessState>
      ) : null}

      {open ? (
        <form
          className="profile-form"
          data-testid="password-form"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <Field
            label={t("pf.pwCurrent")}
            type="password"
            value={form.current}
            data-testid="input-current-password"
            autoComplete="current-password"
            onChange={(e) => setForm((f) => ({ ...f, current: e.target.value }))}
          />
          <Field
            label={t("pf.pwNew")}
            type="password"
            value={form.next}
            hint={t("pf.pwHint")}
            data-testid="input-new-password"
            autoComplete="new-password"
            onChange={(e) => setForm((f) => ({ ...f, next: e.target.value }))}
          />
          <Field
            label={t("pf.pwConfirm")}
            type="password"
            value={form.confirm}
            data-testid="input-confirm-password"
            autoComplete="new-password"
            onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
          />

          {errKey ? (
            <p className="field-error" role="alert" data-testid="password-error">
              {t(errKey)}
            </p>
          ) : null}

          <div className="settings-controls">
            <Button
              type="submit"
              variant="primary"
              data-testid="password-submit"
              disabled={change.isPending}
            >
              {change.isPending ? t("pf.saving") : t("pf.pwSubmit")}
            </Button>
            <Button
              type="button"
              variant="secondary"
              data-testid="password-cancel"
              onClick={cancel}
              disabled={change.isPending}
            >
              {t("pf.cancel")}
            </Button>
          </div>
        </form>
      ) : null}
    </>
  );
}
